import { Button, Card, CardContent, Grid } from "@mui/material";
import SettingsReport from "./SettingsReport";
import {
  PerformanceReportTypeEnum,
  PDFReportRequest,
  PDFReportClientInfoItem,
  PDFReportThemeInfoItem,
  PDFReportTableColumnRow,
  ReportPdfResponse,
  OrionTransaction,
  NewReportThemedCards,
  ReportThemedCardConfig,
  PDFReportValueType,
  PDFReportChartType,
} from "helpers/types";
import {
  getLastPerformanceStockClientId,
  getLastPerformanceStockAccountIds,
} from "helpers";
import ReportQuarterTable, { defaultQuarterRows } from "./QuarterTableReport";
import SingleValueCard from "./SingleValueCard";
import MultiValuesCard from "./MultiValuesCard";
import {
  SingleValueCardColor,
  SingleValueCardType,
  ReportQuarterTableColumn,
  type QuarterRow,
} from "constants/index";
import { ReportValueCardProps } from "helpers/types";
import { useStockReportValue } from "hooks/useReportStorage";
import { OverlayVsStockChart } from "./Charts/OverlayVsStockChart";
import { useMemo, useState, useCallback, useEffect } from "react";
import { useGetReportPdfMutation } from "features/trading/accounts";
import {
  ReportSettingsProvider,
  useReportSettings,
} from "./ReportSettingsContext";
import TransactionModal from "./TransactionModal";
import RequestNotification from "components/ui/RequestNotification";
import {
  ReportSingleValueCardDescriptions,
  ReportSingleValueCardLabels,
  ReportTheamedValueCardLabels,
  ReportTheamedValueCardDescriptions,
  ReportTheamedValueCardValuesLabels,
} from "helpers/constants";
import { useReportCalculation } from "./useReportCalculation";
import { toPdfValues } from "./reportPdfUtils";

const roundLimit = (val: number, isNegative: boolean) => {
  return isNegative ? Math.floor(val) : Math.ceil(val);
};

const STOCK_REPORT_DEFAULTS = {
  STARTING_VALUE: 3000000,
  CURRENT_VALUE: 3600000,
  CUMULATIVE_CALL_INCOME: 210000,
  NET_REALIZED_TLH_LOSSES: 180000,
  OVERLAY_VS_STOCK_ALONE: 1.2,
  OVERLAY_RETURN_TWR: 7.1,
  OVERLAY_CHART_LIMIT: 100,
  OVERLAY_CHART_NEGATIVE_LIMIT: -100,
  STARTING_SHARES: 30000,
  CURRENT_SHARES: 30000,
  STOCK_PRICE_START: 300,
  STOCK_PRICE_NOW: 360,
  STOCK_ONLY_RETURN: 20,
  UPSIDE_PARTICIPATION: 92,
  OPTION_INCOME_PERIOD: 65000,
  DIVIDEND_INCOME_PERIOD: 0,
  CUMULATIVE_INCOME: 210000,
  AVG_ANNUALIZED_YIELD: 7,
  CURRENT_OPTION_BUYBACK: 15000,
  NET_OPTION_INCOME: 195000,
  LOSSES_USED: 60000,
  LOSSES_REMAINING: 120000,
  SHARES_SOLD: 4000,
  SELLABLE_TODAY: 6000,
};

const OVERLAY_CHART_TITLE = "Overlay vs Stock Only (Illustrative)";
const OVERLAY_CHART_SERIES_STOCK_ONLY = "Stock Only";
const OVERLAY_CHART_SERIES_STOCK_OVERLAY = "Stock + Overlay";

function StockReportContent() {
  // SingleValueCard hooks
  const [startingValue, setStartingValue] = useStockReportValue(
    "starting_value_stock",
    STOCK_REPORT_DEFAULTS.STARTING_VALUE,
  );
  const [currentValue, setCurrentValue] = useStockReportValue(
    "current_value_stock",
    STOCK_REPORT_DEFAULTS.CURRENT_VALUE,
  );
  const [cumulativeCallIncome, setCumulativeCallIncome] = useStockReportValue(
    "cumulative_call_income_stock",
    STOCK_REPORT_DEFAULTS.CUMULATIVE_CALL_INCOME,
  );
  const [netRealizedTLHLosses, setNetRealizedTLHLosses] = useStockReportValue(
    "net_realized_tlh_losses_stock",
    STOCK_REPORT_DEFAULTS.NET_REALIZED_TLH_LOSSES,
  );
  const [overlayVsStockAlone, setOverlayVsStockAlone] = useStockReportValue(
    "overlay_vs_stock_alone_stock",
    STOCK_REPORT_DEFAULTS.OVERLAY_VS_STOCK_ALONE,
  );
  const [overlayReturnTWR, setOverlayReturnTWR] = useStockReportValue(
    "overlay_return_twr_stock",
    STOCK_REPORT_DEFAULTS.OVERLAY_RETURN_TWR,
  );

  const [tableRows, setTableRows] = useState<QuarterRow[]>(defaultQuarterRows);
  const [overlayChartLimit, setOverlayChartLimit] = useStockReportValue(
    "overlay_chart_limit_stock",
    STOCK_REPORT_DEFAULTS.OVERLAY_CHART_LIMIT,
  );
  const [overlayChartNegativeLimit, setOverlayChartNegativeLimit] =
    useStockReportValue(
      "overlay_chart_negative_limit_stock",
      STOCK_REPORT_DEFAULTS.OVERLAY_CHART_NEGATIVE_LIMIT,
    );
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [deselectedTransactions, setDeselectedTransactions] = useState<
    OrionTransaction[]
  >([]);
  const [getReportPdf, { isLoading: isPdfLoading }] = useGetReportPdfMutation();
  const { settingsInfo } = useReportSettings();
  const {
    calculate: calculateStockReport,
    error: calculationError,
    setError: setCalculationError,
    historyNotice,
    setHistoryNotice,
    isCalculating,
    isEnsuringHistory,
  } = useReportCalculation({
    fallbackErrorMessage: "Failed to calculate stock report. Please try again.",
    accountId: settingsInfo.account_id ?? null,
    strategyId: settingsInfo.strategy_id ?? null,
  });
  const [excelUrl, setExcelUrl] = useState<string | null>(null);

  // MultiValuesCard hooks - Keep your shares
  const [startingShares, setStartingShares] = useStockReportValue(
    "starting_shares_stock",
    STOCK_REPORT_DEFAULTS.STARTING_SHARES,
  );
  const [currentShares, setCurrentShares] = useStockReportValue(
    "current_shares_stock",
    STOCK_REPORT_DEFAULTS.CURRENT_SHARES,
  );
  const [stockPriceStart, setStockPriceStart] = useStockReportValue(
    "stock_price_start_stock",
    STOCK_REPORT_DEFAULTS.STOCK_PRICE_START,
  );
  const [stockPriceNow, setStockPriceNow] = useStockReportValue(
    "stock_price_now_stock",
    STOCK_REPORT_DEFAULTS.STOCK_PRICE_NOW,
  );
  const [stockOnlyReturn, setStockOnlyReturn] = useStockReportValue(
    "stock_only_return_stock",
    STOCK_REPORT_DEFAULTS.STOCK_ONLY_RETURN,
  );
  const [upsideParticipation, setUpsideParticipation] = useStockReportValue(
    "upside_participation_stock",
    STOCK_REPORT_DEFAULTS.UPSIDE_PARTICIPATION,
  );

  // MultiValuesCard hooks - Generate Income
  const [optionIncomePeriod, setOptionIncomePeriod] = useStockReportValue(
    "option_income_period_stock",
    STOCK_REPORT_DEFAULTS.OPTION_INCOME_PERIOD,
  );
  const [dividendIncomePeriod, setDividendIncomePeriod] = useStockReportValue(
    "dividend_income_period_stock",
    STOCK_REPORT_DEFAULTS.DIVIDEND_INCOME_PERIOD,
  );
  const [cumulativeIncome, setCumulativeIncome] = useStockReportValue(
    "cumulative_income_stock",
    STOCK_REPORT_DEFAULTS.CUMULATIVE_INCOME,
  );
  const [avgAnnualizedYield, setAvgAnnualizedYield] = useStockReportValue(
    "avg_annualized_yield_stock",
    STOCK_REPORT_DEFAULTS.AVG_ANNUALIZED_YIELD,
  );
  const [currentOptionBuyback, setCurrentOptionBuyback] = useStockReportValue(
    "current_option_buyback_stock",
    STOCK_REPORT_DEFAULTS.CURRENT_OPTION_BUYBACK,
  );
  const [netOptionIncome, setNetOptionIncome] = useStockReportValue(
    "net_option_income_stock",
    STOCK_REPORT_DEFAULTS.NET_OPTION_INCOME,
  );

  // MultiValuesCard hooks - Harvest Tax Benefits
  const [lossesUsed, setLossesUsed] = useStockReportValue(
    "losses_used_stock",
    STOCK_REPORT_DEFAULTS.LOSSES_USED,
  );
  const [lossesRemaining, setLossesRemaining] = useStockReportValue(
    "losses_remaining_stock",
    STOCK_REPORT_DEFAULTS.LOSSES_REMAINING,
  );
  const [sharesSold, setSharesSold] = useStockReportValue(
    "shares_sold_stock",
    STOCK_REPORT_DEFAULTS.SHARES_SOLD,
  );
  const [sellableToday, setSellableToday] = useStockReportValue(
    "sellable_today_stock",
    STOCK_REPORT_DEFAULTS.SELLABLE_TODAY,
  );

  const keepValuesWithHooks: ReportValueCardProps[] = [
    {
      label: ReportTheamedValueCardValuesLabels.STARTING_SHARES,
      value: startingShares,
      type: SingleValueCardType.NUMBER,
      onValueChange: setStartingShares,
    },
    {
      label: ReportTheamedValueCardValuesLabels.CURRENT_SHARES,
      value: currentShares,
      type: SingleValueCardType.NUMBER,
      onValueChange: setCurrentShares,
    },
    {
      label: ReportTheamedValueCardValuesLabels.STOCK_PRICE_START,
      value: stockPriceStart,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setStockPriceStart,
    },
    {
      label: ReportTheamedValueCardValuesLabels.STOCK_PRICE_NOW,
      value: stockPriceNow,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setStockPriceNow,
    },
    {
      label: ReportTheamedValueCardValuesLabels.STOCK_ONLY_RETURN,
      value: stockOnlyReturn,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setStockOnlyReturn,
    },
    {
      label: ReportTheamedValueCardValuesLabels.UPSIDE_PARTICIPATION,
      value: upsideParticipation,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setUpsideParticipation,
    },
  ];

  // Update generateIncomeValues with hooks
  const generateIncomeValuesWithHooks: ReportValueCardProps[] = [
    {
      label: ReportTheamedValueCardValuesLabels.OPTION_INCOME_PERIOD,
      value: optionIncomePeriod,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setOptionIncomePeriod,
    },
    {
      label: ReportTheamedValueCardValuesLabels.DIVIDEND_INCOME_PERIOD,
      value: dividendIncomePeriod,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setDividendIncomePeriod,
    },
    {
      label: ReportTheamedValueCardValuesLabels.CUMULATIVE_INCOME,
      value: cumulativeIncome,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setCumulativeIncome,
    },
    {
      label: ReportTheamedValueCardValuesLabels.AVG_ANNUALIZED_YIELD,
      value: avgAnnualizedYield,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setAvgAnnualizedYield,
    },
    {
      label: ReportTheamedValueCardValuesLabels.CURRENT_OPTION_BUYBACK,
      value: currentOptionBuyback,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setCurrentOptionBuyback,
    },
    {
      label: ReportTheamedValueCardValuesLabels.NET_OPTION_INCOME,
      value: netOptionIncome,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setNetOptionIncome,
    },
  ];

  // Update harvestTaxBenefitsValues with hooks
  const harvestTaxBenefitsValuesWithHooks: ReportValueCardProps[] = [
    {
      label: ReportTheamedValueCardValuesLabels.NET_REALIZED_LOSSES,
      value: netRealizedTLHLosses,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setNetRealizedTLHLosses,
    },
    {
      label: ReportTheamedValueCardValuesLabels.LOSSES_USED,
      value: lossesUsed,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setLossesUsed,
    },
    {
      label: ReportTheamedValueCardValuesLabels.LOSSES_REMAINING,
      value: lossesRemaining,
      type: SingleValueCardType.CURRENCY,
      fullWidthRow: true,
      onValueChange: setLossesRemaining,
    },
    {
      label: ReportTheamedValueCardValuesLabels.SHARES_SOLD,
      value: sharesSold,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesSold,
    },
    {
      label: ReportTheamedValueCardValuesLabels.SELLABLE_TODAY,
      value: sellableToday,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSellableToday,
    },
  ];

  // Single source of truth for themed cards: same values drive UI and PDF teamed_info
  const themedCardsConfig = useMemo<ReportThemedCardConfig[]>(
    () => [
      {
        label: ReportTheamedValueCardLabels.KEEP_YOUR_SHARES,
        description: ReportTheamedValueCardDescriptions.KEEP_YOUR_SHARES,
        values: keepValuesWithHooks,
        addictional_component: {
          type: PDFReportChartType.BAR,
          title: OVERLAY_CHART_TITLE,
          limit: overlayChartLimit as number,
          negativeLimit: overlayChartNegativeLimit as number,
          data: [
            {
              label: OVERLAY_CHART_SERIES_STOCK_ONLY,
              value: stockOnlyReturn as number,
              type: PDFReportValueType.PERCENTAGE,
            },
            {
              label: OVERLAY_CHART_SERIES_STOCK_OVERLAY,
              value: overlayReturnTWR as number,
              type: PDFReportValueType.PERCENTAGE,
            },
          ],
        },
      },
      {
        label: ReportTheamedValueCardLabels.GENERATE_INCOME,
        description: ReportTheamedValueCardDescriptions.GENERATE_INCOME,
        values: generateIncomeValuesWithHooks,
        addictional_component: null,
      },
      {
        label: ReportTheamedValueCardLabels.HARVEST_TAX_BENEFITS,
        description: ReportTheamedValueCardDescriptions.HARVEST_TAX_BENEFITS,
        values: harvestTaxBenefitsValuesWithHooks,
        addictional_component: null,
      },
    ],
    [
      keepValuesWithHooks,
      generateIncomeValuesWithHooks,
      harvestTaxBenefitsValuesWithHooks,
      stockOnlyReturn,
      overlayReturnTWR,
      overlayChartLimit,
      overlayChartNegativeLimit,
    ],
  );

  const buildPdfRequest = useCallback((): PDFReportRequest => {
    const table_columns: PDFReportTableColumnRow[] = tableRows.map((row) => ({
      id: row.id,
      Period: row[ReportQuarterTableColumn.Period],
      "Stock Price": row[ReportQuarterTableColumn.StockPrice],
      "Option Income": row[ReportQuarterTableColumn.OptionIncome],
      "Dividend Income": row[ReportQuarterTableColumn.DividenIncome],
      "Total Income": row[ReportQuarterTableColumn.TotalIncome],
      Comment: row[ReportQuarterTableColumn.Comment],
    }));

    const client_info: PDFReportClientInfoItem[] = [
      {
        label: ReportSingleValueCardLabels.STARTING_VALUE,
        value: String(startingValue),
        type: PDFReportValueType.CURRENCY,
        description: ReportSingleValueCardDescriptions.STARTING_VALUE,
      },
      {
        label: ReportSingleValueCardLabels.CURRENT_VALUE,
        value: String(currentValue),
        type: PDFReportValueType.CURRENCY,
        description: ReportSingleValueCardDescriptions.CURRENT_VALUE,
      },
      {
        label: ReportSingleValueCardLabels.CUMULATIVE_CALL_INCOME,
        value: String(cumulativeCallIncome),
        type: PDFReportValueType.CURRENCY,
        description: ReportSingleValueCardDescriptions.CUMULATIVE_CALL_INCOME,
      },
      {
        label: ReportSingleValueCardLabels.NET_REALIZED_TLH_LOSSES,
        value: String(netRealizedTLHLosses),
        type: PDFReportValueType.CURRENCY,
        description: ReportSingleValueCardDescriptions.NET_REALIZED_TLH_LOSSES,
      },
      {
        label: ReportSingleValueCardLabels.OVERLAY_VS_STOCK_ALONE,
        value: String(overlayVsStockAlone),
        type: PDFReportValueType.PERCENTAGE,
        description: ReportSingleValueCardDescriptions.OVERLAY_VS_STOCK_ALONE,
      },
      {
        label: ReportSingleValueCardLabels.OVERLAY_RETURN_TWR,
        value: String(overlayReturnTWR),
        type: PDFReportValueType.PERCENTAGE,
        description: ReportSingleValueCardDescriptions.OVERLAY_RETURN_TWR,
      },
    ];

    const theme_info: PDFReportThemeInfoItem[] = themedCardsConfig.map(
      (card) => ({
        label: card.label,
        description: card.description,
        values: toPdfValues(card.values),
        addictional_component: card.addictional_component,
      }),
    );

    return {
      type: "stock",
      settings_info: settingsInfo,
      table_columns,
      client_info,
      theme_info,
    };
  }, [
    tableRows,
    themedCardsConfig,
    settingsInfo,
    startingValue,
    currentValue,
    cumulativeCallIncome,
    netRealizedTLHLosses,
    overlayVsStockAlone,
    overlayReturnTWR,
  ]);

  const handleDownloadPdf = useCallback(async () => {
    try {
      const response: ReportPdfResponse =
        await getReportPdf(buildPdfRequest()).unwrap();
      if (response.download_url) {
        const a = document.createElement("a");
        a.href = response.download_url;
        a.click();
        a.remove();
        return;
      }
      throw new Error("Failed to download PDF");
    } catch (error) {
      throw new Error("Failed to download PDF");
    }
  }, [getReportPdf, buildPdfRequest]);

  const openTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(true);
  }, []);

  const closeTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(false);
  }, []);

  const handleCalculate = useCallback(async () => {
    const [start_date, end_date] = settingsInfo.period;
    const globalCumulativePeriod =
      settingsInfo.global_cumulative_period?.[0] &&
      settingsInfo.global_cumulative_period?.[1]
        ? settingsInfo.global_cumulative_period
        : undefined;

    if (
      !settingsInfo.orion_account_id ||
      !settingsInfo.strategy_id ||
      !start_date ||
      !end_date
    ) {
      setCalculationError(
        "Cannot calculate report: missing account, strategy, or date range.",
      );
      return;
    }

    const data = await calculateStockReport({
      orion_account_id: settingsInfo.orion_account_id,
      strategy_id: settingsInfo.strategy_id,
      start_date,
      end_date,
      ...(globalCumulativePeriod
        ? { global_cumulative_period: globalCumulativePeriod }
        : {}),
      report_type: "stock",
      exclude_transactions:
        deselectedTransactions.length > 0
          ? deselectedTransactions.map((transaction) => transaction.id)
          : undefined,
    });
    if (!data) return;

    const cards = data.single_info_cards;
    setStartingValue(cards.card_1);
    setCurrentValue(cards.card_2);
    setCumulativeCallIncome(cards.card_3);
    // TODO: Add net realized TLH losses and overlay vs stock alone after the discussion with clients
    // setNetRealizedTLHLosses(cards.card_4);
    setOverlayVsStockAlone(cards.card_5);
    setOverlayReturnTWR(cards.card_6);

    // Apply themed card values from backend response when present.
    // Missing values keep current local-storage state.
    const themedCards = data.themed_cards as NewReportThemedCards | undefined;
    if (themedCards) {
      setStartingShares(
        themedCards.keep_your_shares?.starting_shares ??
          (startingShares as number),
      );
      setCurrentShares(
        themedCards.keep_your_shares?.current_shares ??
          (currentShares as number),
      );
      setStockPriceStart(
        themedCards.keep_your_shares?.stock_price_start ??
          (stockPriceStart as number),
      );
      setStockPriceNow(
        themedCards.keep_your_shares?.stock_price_now ??
          (stockPriceNow as number),
      );
      setStockOnlyReturn(
        themedCards.keep_your_shares?.stock_only_return ??
          (stockOnlyReturn as number),
      );
      setUpsideParticipation(
        themedCards.keep_your_shares?.upside_participation ??
          (upsideParticipation as number),
      );

      setOptionIncomePeriod(
        themedCards.generate_income?.option_income_period ??
          (optionIncomePeriod as number),
      );
      setDividendIncomePeriod(
        themedCards.generate_income?.dividend_income_period ??
          (dividendIncomePeriod as number),
      );
      setCumulativeIncome(
        themedCards.generate_income?.cumulative_income ??
          (cumulativeIncome as number),
      );
      setAvgAnnualizedYield(
        themedCards.generate_income?.avg_annualized_yield ??
          (avgAnnualizedYield as number),
      );
      setCurrentOptionBuyback(
        themedCards.generate_income?.current_option_buyback ??
          (currentOptionBuyback as number),
      );
      setNetOptionIncome(
        themedCards.generate_income?.net_option_income ??
          (netOptionIncome as number),
      );

      setNetRealizedTLHLosses(
        themedCards.harvest_tax_benefits?.net_realized_tlh_losses ??
          (netRealizedTLHLosses as number),
      );
      setLossesUsed(
        themedCards.harvest_tax_benefits?.losses_used ?? (lossesUsed as number),
      );
      setLossesRemaining(
        themedCards.harvest_tax_benefits?.losses_remaining ??
          (lossesRemaining as number),
      );
      setSharesSold(
        themedCards.harvest_tax_benefits?.shares_sold ?? (sharesSold as number),
      );
      setSellableToday(
        themedCards.harvest_tax_benefits?.sellable_today ??
          (sellableToday as number),
      );
    }

    // TODO: After types are fixed, remove the conversion to numbers
    const stockOnlyReturnForCompare =
      themedCards?.keep_your_shares?.stock_only_return ?? stockOnlyReturn;

    const isNegative =
      Number(stockOnlyReturnForCompare) < 0 && Number(cards.card_6) < 0;

    if (isNegative) {
      setOverlayChartNegativeLimit(
        roundLimit(
          Math.min(Number(stockOnlyReturnForCompare), Number(cards.card_6)),
          isNegative,
        ),
      );
    } else {
      setOverlayChartLimit(
        roundLimit(
          Math.max(Number(stockOnlyReturnForCompare), Number(cards.card_6)),
          isNegative,
        ),
      );
    }

    // Map backend table_data into quarter table rows, preserving existing comments if present.
    const backendRows = data.graph_or_table_data as any[]; // STOCK case returns table_data
    const nextTableRows: QuarterRow[] = backendRows.map(
      (row, index): QuarterRow => ({
        id: index + 1,
        [ReportQuarterTableColumn.Period]: row.header_quarter,
        [ReportQuarterTableColumn.StockPrice]: row.stock_price,
        [ReportQuarterTableColumn.OptionIncome]: row.option_income,
        [ReportQuarterTableColumn.DividenIncome]: row.dividend_income,
        [ReportQuarterTableColumn.TotalIncome]: row.total_income,
        // Keep existing comment (or default) so user-edited text is preserved
        [ReportQuarterTableColumn.Comment]:
          tableRows[index]?.[ReportQuarterTableColumn.Comment] ??
          defaultQuarterRows[index]?.[ReportQuarterTableColumn.Comment] ??
          "",
      }),
    );
    setTableRows(nextTableRows);
    setExcelUrl(data.excel_url ?? null);
  }, [
    settingsInfo.period,
    settingsInfo.global_cumulative_period,
    settingsInfo.orion_account_id,
    settingsInfo.strategy_id,
    deselectedTransactions,
    calculateStockReport,
    setStartingValue,
    setCurrentValue,
    setCumulativeCallIncome,
    setOverlayReturnTWR,
    setStartingShares,
    setCurrentShares,
    setStockPriceStart,
    setStockPriceNow,
    setStockOnlyReturn,
    setUpsideParticipation,
    setOptionIncomePeriod,
    setDividendIncomePeriod,
    setCumulativeIncome,
    setAvgAnnualizedYield,
    setCurrentOptionBuyback,
    setNetOptionIncome,
    setNetRealizedTLHLosses,
    setLossesUsed,
    setLossesRemaining,
    setSharesSold,
    setSellableToday,
    startingShares,
    currentShares,
    stockPriceStart,
    stockPriceNow,
    stockOnlyReturn,
    upsideParticipation,
    optionIncomePeriod,
    dividendIncomePeriod,
    cumulativeIncome,
    avgAnnualizedYield,
    currentOptionBuyback,
    netOptionIncome,
    lossesUsed,
    lossesRemaining,
    sharesSold,
    sellableToday,
    netRealizedTLHLosses,
    setCalculationError,
  ]);

  const handleDownloadExcel = useCallback(() => {
    if (!excelUrl) return;
    const a = document.createElement("a");
    a.href = excelUrl;
    a.click();
    a.remove();
  }, [excelUrl]);

  // Reset Excel URL when key report inputs change
  useEffect(() => {
    setExcelUrl(null);
  }, [
    settingsInfo.orion_account_id,
    settingsInfo.strategy_id,
    settingsInfo.period?.[0],
    settingsInfo.period?.[1],
    settingsInfo.global_cumulative_period?.[0],
    settingsInfo.global_cumulative_period?.[1],
  ]);

  return (
    <Card>
      <CardContent>
        <RequestNotification
          open={Boolean(calculationError)}
          title="Calculation failed"
          message={calculationError}
          onClose={() => setCalculationError("")}
        />
        <RequestNotification
          open={Boolean(historyNotice)}
          severity={historyNotice?.status}
          title={
            historyNotice?.status === "success"
              ? "History created"
              : "Couldn't create account history"
          }
          message={historyNotice?.message ?? ""}
          onClose={() => setHistoryNotice(null)}
        />
        <SettingsReport
          type={PerformanceReportTypeEnum.STOCK}
          header="Optic Single Stock"
          subtitle="Covered Call & Tax-Loss Harvesting Overlay"
        />
        <Grid container justifyContent="center" sx={{ mt: 1 }}>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPdf}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Generating…" : "Download PDF"}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadExcel}
              disabled={!excelUrl}
            >
              {excelUrl ? "Download Excel" : "Download Excel"}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCalculate}
              disabled={isPdfLoading || isCalculating || isEnsuringHistory}
            >
              {isEnsuringHistory
                ? "Preparing history…"
                : isCalculating
                  ? "Calculating…"
                  : `Calculate`}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={openTransactionModal}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Updating…" : "Transactions"}
            </Button>
          </Grid>
        </Grid>
        <ReportQuarterTable rows={tableRows} onRowsChange={setTableRows} />
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={startingValue as number}
              label={ReportSingleValueCardLabels.STARTING_VALUE}
              description={ReportSingleValueCardDescriptions.STARTING_VALUE}
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setStartingValue}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={currentValue as number}
              label={ReportSingleValueCardLabels.CURRENT_VALUE}
              description={ReportSingleValueCardDescriptions.CURRENT_VALUE}
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setCurrentValue}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={cumulativeCallIncome as number}
              label={ReportSingleValueCardLabels.CUMULATIVE_CALL_INCOME}
              description={
                ReportSingleValueCardDescriptions.CUMULATIVE_CALL_INCOME
              }
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setCumulativeCallIncome}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={netRealizedTLHLosses as number}
              label={ReportSingleValueCardLabels.NET_REALIZED_TLH_LOSSES}
              description={
                ReportSingleValueCardDescriptions.NET_REALIZED_TLH_LOSSES
              }
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.ERROR}
              onValueChange={setNetRealizedTLHLosses}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={overlayVsStockAlone as number}
              label={ReportSingleValueCardLabels.OVERLAY_VS_STOCK_ALONE}
              description={
                ReportSingleValueCardDescriptions.OVERLAY_VS_STOCK_ALONE
              }
              type={SingleValueCardType.PERCENTAGE}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setOverlayVsStockAlone}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={overlayReturnTWR as number}
              label={ReportSingleValueCardLabels.OVERLAY_RETURN_TWR}
              description={ReportSingleValueCardDescriptions.OVERLAY_RETURN_TWR}
              type={SingleValueCardType.PERCENTAGE}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setOverlayReturnTWR}
            />
          </Grid>
        </Grid>
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          {themedCardsConfig.map((card, index) => (
            <Grid item lg={6} xs={12} md={6} key={card.label}>
              <MultiValuesCard
                title={card.label}
                values={card.values}
                description={card.description}
                additionalComponents={
                  index === 0 ? (
                    <OverlayVsStockChart
                      stockOnlyValue={stockOnlyReturn as number}
                      stockOverlayValue={overlayReturnTWR as number}
                      limit={overlayChartLimit as number}
                      negativeLimit={overlayChartNegativeLimit as number}
                      onLimitChange={setOverlayChartLimit}
                      onNegativeLimitChange={setOverlayChartNegativeLimit}
                    />
                  ) : null
                }
              />
            </Grid>
          ))}
        </Grid>
        <TransactionModal
          open={isTransactionModalOpen}
          onClose={closeTransactionModal}
          settingsInfo={settingsInfo}
          reportType={PerformanceReportTypeEnum.STOCK}
          onDeselectedTransactionsChange={setDeselectedTransactions}
        />
      </CardContent>
    </Card>
  );
}

export const StockReport = () => (
  <ReportSettingsProvider>
    <StockReportContent />
  </ReportSettingsProvider>
);

export default StockReport;
